/**
 * Feedback triage (Card P244) — the admin side of the in-CRM report button.
 *
 * Gated on feedback:triage. Everything this page reads is audited server-side,
 * and the screenshot is audited SEPARATELY from the row that holds it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

/*
 * The lifecycle (Card P245, migration 208). ⛔ One column, one vocabulary —
 * the 207 spellings ('new', 'card_written', 'in_progress', 'in_review',
 * 'live') no longer exist in the database and must not reappear here.
 */
const STATUSES = [
  'reported', 'triaged', 'diagnosed', 'fix_pr', 'merged',
  'released', 'verified', 'closed', 'reopened', 'wont_do',
];
const STATUS_LABEL = {
  reported: 'Reported', triaged: 'Triaged', diagnosed: 'Diagnosed',
  fix_pr: 'Fix PR open', merged: 'Merged', released: 'Released',
  verified: 'Verified', closed: 'Closed', reopened: 'Reopened',
  wont_do: "Won't do",
};
const AREAS = [
  'payments', 'pipeline', 'ai_report', 'scheduling', 'clinical', 'inventory',
  'portal', 'messaging', 'reporting', 'auth', 'other',
];
const TYPE_LABEL = { problem: 'Problem', change: 'Change', idea: 'New idea', question: 'Question' };
const PRIORITY_LABEL = { urgent: 'Urgent', soon: 'Soon', when_possible: 'When possible' };

function apiOf() {
  return (typeof window !== 'undefined' && window.zignagenixAPI) || null;
}

/** ET, because that is where Daniel and Carlos are. */
function whenET(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/New_York', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (e) { return String(iso); }
}

export default function FeedbackTriagePage({ user }) {
  const canTriage = !!(user && Array.isArray(user.permissions) && user.permissions.includes('feedback:triage'));

  const [filters, setFilters] = useState({ status: '', priority: '', requestType: '', area: '', q: '', scope: '' });
  /*
   * ⛔ THE SEARCH BOX IS DEBOUNCED, AND IT IS NOT A POLISH DETAIL.
   *
   * A text search cannot be a SQL predicate here — the report columns hold
   * AES-GCM envelopes — so the server filters on the clear columns and then
   * DECRYPTS the matching page to search it. Re-querying on every keystroke
   * would decrypt up to the scan limit once per character typed, which turns
   * a four-word search into thousands of AES operations and a queue of
   * in-flight requests whose answers can arrive out of order.
   *
   * `typed` is what the input shows; `filters.q` is what the server is asked
   * for, and only the latter is in load()'s dependencies.
   */
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === typed ? f : { ...f, q: typed })), 300);
    return () => clearTimeout(t);
  }, [typed]);
  const [counts, setCounts] = useState(null);   // { scanned, matched, truncated }
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);     // full detail
  const [checked, setChecked] = useState({});          // reference -> bool
  const [shotUrl, setShotUrl] = useState(null);
  const [shotError, setShotError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const api = apiOf();
    if (!api) return;
    setError(null);
    try {
      const res = await api.invoke('feedback:list', {
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        requestType: filters.requestType || undefined,
        area: filters.area || undefined,
        q: filters.q || undefined,
        scope: filters.scope || undefined,
      });
      if (res && res.error) { setError(res.message || 'That list could not be loaded.'); setRows([]); setCounts(null); return; }
      setRows((res && res.requests) || []);
      // ⚠ Both numbers, always. A text search runs after decryption over a
      // bounded page, so "how many were looked at" is part of the answer.
      setCounts(res ? { scanned: res.scanned, matched: res.matched, truncated: !!res.truncated } : null);
    } catch (e) {
      setError('That list could not be loaded — the connection failed.');
      setRows([]);
    }
  }, [filters]);

  useEffect(() => { if (canTriage) load(); }, [canTriage, load]);

  const open = useCallback(async (reference) => {
    const api = apiOf();
    if (!api) return;
    setShotUrl(null); setShotError(null);
    try {
      const res = await api.invoke('feedback:get', { reference });
      if (res && res.error) { setError(res.message); return; }
      setSelected(res.request);
    } catch (e) { setError('That request could not be opened.'); }
  }, []);

  const showScreenshot = useCallback(async () => {
    const api = apiOf();
    if (!api || !selected) return;
    setShotError(null);
    try {
      const res = await api.invoke('feedback:screenshot', { reference: selected.reference });
      if (res && res.error) { setShotError(res.message); return; }
      setShotUrl(res.url);
    } catch (e) { setShotError('The picture could not be opened.'); }
  }, [selected]);

  const save = useCallback(async (patch) => {
    const api = apiOf();
    if (!api || !selected) return;
    try {
      const res = await api.invoke('feedback:update', { reference: selected.reference, ...patch });
      if (res && res.error) { setError(res.message); return; }
      setSelected((s) => ({ ...s, ...patch }));
      load();
    } catch (e) { setError('That change could not be saved.'); }
  }, [selected, load]);

  const selectedRefs = useMemo(
    () => Object.keys(checked).filter((k) => checked[k]), [checked],
  );

  /**
   * ⛔ THE BUNDLE IS BUILT SERVER-SIDE, NOT HERE.
   *
   * The client holds the list rows, so assembling the text in the browser
   * would be one fewer round-trip — and would put the redaction on the side of
   * the wire that the exported rows never passed through. The server has the
   * full text, the hardened redactor, and the audit row. The client asks and
   * copies.
   */
  const copyForClaude = useCallback(async () => {
    const api = apiOf();
    if (!api || !selectedRefs.length) return;
    try {
      const res = await api.invoke('feedback:export', { references: selectedRefs });
      if (res && res.error) { setError(res.message); return; }
      await navigator.clipboard.writeText(res.text);
      setCopied(`${res.count} request${res.count === 1 ? '' : 's'} copied — paste into Cowork.`);
      window.setTimeout(() => setCopied(null), 4000);
    } catch (e) {
      setError('That could not be copied. Your browser may be blocking clipboard access.');
    }
  }, [selectedRefs]);

  if (!canTriage) {
    return (
      <div className="page">
        <h1>Feedback triage</h1>
        <p className="fb-muted">You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div className="page fb-triage">
      <h1>Feedback triage</h1>

      <div className="fb-filters">
        <select aria-label="Status" value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select aria-label="Priority" value={filters.priority}
          onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}>
          <option value="">All priorities</option>
          {Object.keys(PRIORITY_LABEL).map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
        </select>
        <input type="search" aria-label="Search requests" placeholder="Search text, R-number, card, PR…"
          value={typed}
          onChange={(e) => setTyped(e.target.value)} />
        <select aria-label="Area" value={filters.area}
          onChange={(e) => setFilters((f) => ({ ...f, area: e.target.value }))}>
          <option value="">All areas</option>
          {AREAS.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
        <label className="fb-scope">
          <input type="checkbox" aria-label="Include closed requests"
            checked={filters.scope === 'all'}
            onChange={(e) => setFilters((f) => ({ ...f, scope: e.target.checked ? 'all' : '' }))} />
          {' '}Include closed
        </label>
        <select aria-label="Type" value={filters.requestType}
          onChange={(e) => setFilters((f) => ({ ...f, requestType: e.target.value }))}>
          <option value="">All types</option>
          {Object.keys(TYPE_LABEL).map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
        </select>
        <button type="button" className="fb-primary" onClick={copyForClaude}
          disabled={!selectedRefs.length}>
          Copy for Claude{selectedRefs.length ? ` (${selectedRefs.length})` : ''}
        </button>
        {copied && <span className="fb-copied" role="status">{copied}</span>}
      </div>

      {error && <p className="fb-error" role="alert">{error}</p>}

      <div className="responsive-scroll">
        {counts && (
          <p className="fb-counts" role="status">
            {counts.matched} shown · {counts.scanned} scanned
            {counts.truncated && ' · ⚠ more matches exist beyond the search limit — narrow the filters'}
          </p>
        )}

        <table className="fb-table" style={{ minWidth: 760 }}>
          <thead>
            <tr>
              <th scope="col" aria-label="Select" />
              <th scope="col">Ref</th>
              <th scope="col">When</th>
              <th scope="col">Type</th>
              <th scope="col">Priority</th>
              <th scope="col">Status</th>
              <th scope="col">Page</th>
              <th scope="col">What happened</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="fb-muted">Loading…</td></tr>}
            {rows && rows.length === 0 && (
              <tr><td colSpan={8} className="fb-muted">No requests match these filters.</td></tr>
            )}
            {(rows || []).map((r) => (
              <tr key={r.id} className={r.priority === 'urgent' ? 'fb-row fb-row--urgent' : 'fb-row'}>
                <td>
                  <input type="checkbox" aria-label={`Select ${r.reference}`}
                    checked={!!checked[r.reference]}
                    onChange={(e) => setChecked((c) => ({ ...c, [r.reference]: e.target.checked }))} />
                </td>
                <td>
                  <button type="button" className="fb-link" onClick={() => open(r.reference)}>
                    {r.reference}
                  </button>
                </td>
                <td>{whenET(r.created_at)}</td>
                <td>{TYPE_LABEL[r.request_type] || r.request_type}</td>
                <td>{PRIORITY_LABEL[r.priority] || r.priority}</td>
                <td><span className={`fb-pill fb-pill--${r.status}`}>{STATUS_LABEL[r.status]}</span></td>
                <td>{r.page_route || '—'}</td>
                <td className="fb-cell-summary">{r.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fb-detail">
          <div className="fb-head">
            <h2>{selected.reference} — {TYPE_LABEL[selected.request_type]} / {PRIORITY_LABEL[selected.priority]}</h2>
            <button type="button" className="fb-x" onClick={() => setSelected(null)} aria-label="Close">×</button>
          </div>

          <dl className="fb-meta">
            <dt>Reported</dt><dd>{whenET(selected.created_at)} ET by {selected.reporter_name} ({selected.user_role})</dd>
            <dt>Page</dt><dd>{selected.page_route || '—'}</dd>
            <dt>Version</dt><dd>{selected.app_version || 'unknown'} {selected.app_commit ? `(${selected.app_commit})` : ''}</dd>
            <dt>Browser</dt><dd className="fb-wrap">{selected.browser || 'unknown'} · {selected.screen_size || ''}</dd>
          </dl>

          <h3>What happened</h3>
          <p className="fb-wrap">{selected.summary}</p>
          {selected.expected && (<><h3>What they expected</h3><p className="fb-wrap">{selected.expected}</p></>)}

          <h3>Before it happened</h3>
          <ol className="fb-trail">
            {(selected.breadcrumbs || []).map((b, i) => (
              <li key={i}><code>{b.kind}</code> {b.label}</li>
            ))}
            {!(selected.breadcrumbs || []).length && <li className="fb-muted">Nothing recorded.</li>}
          </ol>

          <h3>API calls</h3>
          <ol className="fb-trail">
            {(selected.api_calls || []).map((a, i) => (
              <li key={i} className={a.ok ? '' : 'fb-bad'}>
                <code>{a.channel}</code> {a.ok ? 'ok' : `FAILED ${a.status || ''}`} {a.ms != null ? `· ${a.ms}ms` : ''}
              </li>
            ))}
            {!(selected.api_calls || []).length && <li className="fb-muted">Nothing recorded.</li>}
          </ol>

          <h3>Errors</h3>
          <ol className="fb-trail">
            {(selected.console_errors || []).map((e, i) => (
              <li key={i}><strong>{e.message}</strong>{e.stack ? <pre className="fb-stack">{e.stack}</pre> : null}</li>
            ))}
            {!(selected.console_errors || []).length && <li className="fb-muted">No errors recorded.</li>}
          </ol>

          <h3>Screenshot</h3>
          {selected.has_screenshot === false || (!selected.screenshot_key && !selected.has_screenshot) ? (
            <p className="fb-muted">No screenshot was attached.</p>
          ) : shotUrl ? (
            <img className="fb-shot-img" src={shotUrl} alt={`Screen at the time of ${selected.reference}`} />
          ) : (
            <>
              <button type="button" className="fb-secondary" onClick={showScreenshot}>
                Show the screenshot
              </button>
              <p className="fb-muted fb-shot-note">
                Opening the picture is recorded separately in the audit log.
              </p>
            </>
          )}
          {shotError && <p className="fb-warn">{shotError}</p>}

          <h3>Triage</h3>
          <div className="fb-triage-editor">
            <label htmlFor="fb-status">Status</label>
            <select id="fb-status" value={selected.status}
              onChange={(e) => save({ status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>

            <label htmlFor="fb-card">Card #</label>
            <input id="fb-card" defaultValue={selected.card_number || ''}
              onBlur={(e) => save({ cardNumber: e.target.value })} />

            <label htmlFor="fb-pr">PR #</label>
            <input id="fb-pr" defaultValue={selected.pr_number || ''}
              onBlur={(e) => save({ prNumber: e.target.value })} />

            <label htmlFor="fb-notes">Notes</label>
            <textarea id="fb-notes" rows={3} defaultValue={selected.triage_notes || ''}
              onBlur={(e) => save({ triageNotes: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
}
